import React from "react"
import { useTranslation } from "react-i18next"

import * as BikeApi from "features/bike/api/BikeApi"

import { Button, Modal, Spinner } from "components/Elements"
import { StatusCardProps } from "features/bike/models/bikePage"
import { formStatusEnum } from "components/Form"
import { useAlert, useForm } from "hooks"
import { ErrorResponse, OperatorData } from "models"
import { BIKE_STATUS_SOLD } from "utils"

export const RefundStatusCard: React.FC<StatusCardProps> = ({ bike, refreshBike }) => {
	const { formStatus, setFormStatus } = useForm({
		defaultFormStatus: formStatusEnum.ready,
	})
	const [isModalOpen, setModalIsOpen] = React.useState<boolean>(false)

	const isLoading = formStatus === formStatusEnum.loading
	const isReady = formStatus === formStatusEnum.ready

	const { t } = useTranslation()
	const { alert } = useAlert()

	const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()

		try {
			// prevent form to be submitted again
			setFormStatus(formStatusEnum.loading)

			const operatorData: OperatorData = {
				...bike.operator_data,
				stolen_date: null,
			}

			const updatedBikePayload = {
				bike_id: bike.bike_id,
				row_version: bike.row_version,
				bike_status: BIKE_STATUS_SOLD,
				operator_data: operatorData,
			}

			const { getError } = await BikeApi.updateBike(updatedBikePayload)
			if (getError()) throw getError()

			alert("success", t("info_update", { ns: "form" }))
			await refreshBike()
		} catch (error) {
			console.error(error)
			const errorMsg = (error as ErrorResponse)?.message?.toString()
			alert("error", errorMsg)
			setFormStatus(formStatusEnum.ready)
		}
	}

	return (
		<>
			<div className="w-full lg:w-1/2 xl:w-1/4">
				<div className="c-status-card p-5 mb-6 mx-3">
					<p className="text-m font-bold mb-6">
						{t("bike_state.refund_status", {
							ns: "bike",
						})}
					</p>
					<Button
						variant="primary"
						type="submit"
						onClick={() => setModalIsOpen((prevIsModalOpen) => !prevIsModalOpen)}
						className="px-4 py-1"
						size="small"
					>
						{t("bike_state.refund_button", {
							ns: "bike",
						})}
					</Button>
				</div>
			</div>

			<Modal
				title={t("bike_state.refund_modal_title", {
					ns: "bike",
				})}
				isOpen={isModalOpen}
				setIsOpen={setModalIsOpen}
				className={"max-w-2xl"}
			>
				<p className="text-center max-md:px-3 md:px-6 my-8">
					{t("bike_state.refund_modal_description", {
						ns: "bike",
					})}
				</p>

				{isReady && (
					<div className="flex justify-center">
						<Button
							onClick={handleSubmit}
							className="mx-2 max-md:px-10 md:px-20 py-1"
							variant="secondary"
						>
							{t("bike_state.refund_modal_confirm", {
								ns: "bike",
							})}
						</Button>
						<Button
							onClick={() => setModalIsOpen(false)}
							className="mx-2 px-8 py-1 min-w-0"
							variant="secondaryBordered"
						>
							{t("global.cancel")}
						</Button>
					</div>
				)}

				{isLoading && (
					<div className="flex justify-center my-8">
						<Spinner color={"var(--primaryColor)"} size={"100px"} />
					</div>
				)}
			</Modal>
		</>
	)
}
